import React, {useEffect, useRef, useState} from "react";
import {Carousel} from 'react-responsive-carousel';
import AppInfo from "../../bricks/AppInfo";
import {axiosInstance} from "../../axios";
import useTranslation from "../../hooks/useTranslation";
import Loading from "../../bricks/Loading";
import ErrorMessage from "../../bricks/ErrorMessage";
import Button from "../../bricks/form/Button";
import TestQuestion from "./TestQuestion";
import TestOpenQuestion from "./TestOpenQuestion";
import TestAudioQuestion from "./TestAudioQuestion";
import {useUserData} from "../../context/UserContext";
import {useAppData} from "../../context/AppDataContext";
import {ReactComponent as Prev} from "../../assets/prev.svg";
import {ReactComponent as Next} from "../../assets/next.svg";

import "./Test.scss";

const Test = ({testIndex, testType = "sortingTest", testId = null, disableSkipButton = false}) => {
    const {t} = useTranslation();
    const [isLoading, setIsLoading] = useState(true);
    const [testQuestionGroups, setTestQuestionGroups] = useState();
    const [error, setError] = useState();
    const [answerMap, setAnswerMap] = useState(null);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const {
        addTestResult,
        endOtherTest,
        setTestToComplete,
        setIsSortingTestFinished,
        handleTestStart,
        handleTestEnd,
        handleTestUpdate
    } = useUserData();
    const initIndexDoneRef = useRef(false);   // hlídač „počáteční index už nastaven“
    const {data} = useAppData();
    const debounceTimer = useRef();

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const existing = await handleTestStart(data.period.period, testType, testIndex, testId);
                if (mounted) setAnswerMap(existing || {});
            } catch (e) {
                console.log(e);
                setError(e);
            }
        })();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [testType, testIndex, testId, data.period.period]);

    useEffect(() => {
        let mounted = true;
        setIsLoading(true);
        initIndexDoneRef.current = false; // nový test = znovu nastavíme počáteční slide

        (async () => {
            try {
                const {data: qData} = await axiosInstance.get(
                    `/api/test/${testType}/${testIndex}${testId ? `/${testId}` : ''}`
                );
                if (!mounted) return;
                const groups = groupQuestions(qData);
                setTestQuestionGroups(groups);
            } catch (e) {
                if (mounted) {
                    console.log(e);
                    setError(e);
                }
            } finally {
                if (mounted) setIsLoading(false);
            }
        })();

        return () => {
            mounted = false;
        };
    }, [testType, testIndex, testId]);

    useEffect(() => {
        if (!testQuestionGroups || !answerMap) return;
        if (initIndexDoneRef.current) return;

        const groupKeys = Object.keys(testQuestionGroups);
        const firstNonAnswered = groupKeys.findIndex(key =>
            testQuestionGroups[key].some(q => !answerMap[q._id])
        );

        setCurrentQuestionIndex(firstNonAnswered >= 0 ? firstNonAnswered : 0);
        initIndexDoneRef.current = true;
    }, [testQuestionGroups, answerMap]);

    const groupQuestions = (questionList) => {
        const tmp = {};
        questionList.forEach(question => {
            if (question.group) {
                if (tmp[question.group]) {
                    tmp[question.group].push(question);
                } else {
                    tmp[question.group] = [question];
                }
            } else {
                tmp[question._id] = [question];
            }
        });
        return tmp;
    }

    const handleAnswerMapUpdate = (name, value) => {
        setAnswerMap(prev => {
            const next = {...(prev || {}), [name]: {value}};
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
            debounceTimer.current = setTimeout(() => {
                handleTestUpdate(next);
            }, 500);
            return next;
        });
    };
    useEffect(() => () => {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
    }, []);


    const onAnswerChange = (questionsPerView, questionType) => {
        return (name, value) => {
            if (questionsPerView === 1 && questionType === 0) {
                console.log("onAnswerChange", currentQuestionIndex + 1);
                setCurrentQuestionIndex(currentQuestionIndex + 1);
            }
            handleAnswerMapUpdate(name, value);
        }
    }

    const _handleSubmit = async (skipTest = false) => {
        setIsLoading(true);
        if (skipTest) {
            if (!window.confirm('skipTestAlert')) {
                return;
            }
        }
        try {
            const body = {
                testIndex,
                answerMap,
                testType,
                ...testId && {testId}
            };
            handleTestEnd();
            setCurrentQuestionIndex(0);
            const {data} = await axiosInstance.post("/api/test/evaluate", body);
            if (data.testToComplete) {
                setTestToComplete({testType: 'sortingTest', testIndex: data.testToComplete});
                // if there is testToComplete, we want to reset the currentQuestionIndex to 0
            } else {
                setTestToComplete(null);
                setIsSortingTestFinished(true);
            }
            addTestResult(data.testResult);
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
            endOtherTest();
        }
    }

    const renderQuestions = () => {
        const items = [];
        let i = 1;
        Object.values(testQuestionGroups).forEach((testQuestionGroup) => {
            const questionList = [];
            testQuestionGroup.forEach(testQuestion => {
                const commonProps = {
                    question: `${i}. ${testQuestion.question}`,
                    onChange: onAnswerChange(testQuestionGroup.length, testQuestion.type),
                    className: "question-wrapper",
                    answer: answerMap?.[testQuestion._id]?.value,
                    testQuestion
                };
                i++;

                if (testQuestion.type === 0) {
                    questionList.push(<TestQuestion {...commonProps} />);
                } else if (testQuestion.type === 1) {
                    questionList.push(<TestOpenQuestion {...commonProps} onChange={handleAnswerMapUpdate}/>);
                } else if (testQuestion.type === 2) {
                    questionList.push(<TestAudioQuestion {...commonProps} record={testQuestion.record}/>);
                }
            });
            items.push(<>{React.Children.toArray(questionList)}</>);
        });

        return React.Children.toArray(items);
    }

    const countPlaceholders = (qText) => {
        if (!qText) return 0;
        const m = qText.match(/%s/g);
        return m ? m.length : 0;
    };

    const isQuestionAnswered = (q) => {
        const ans = answerMap?.[q._id]?.value;

        // open question
        if (q.type === 1) {
            const needed = countPlaceholders(q.question);
            if (needed === 0) return false; // pokud by bylo 0, bereme jako nekompletní
            if (!ans || typeof ans !== "object") return false;
            for (let i = 0; i < needed; i++) {
                const v = ans?.[i];
                if (!v || String(v).trim() === "") return false;
            }
            return true;
        }

        // single-choice / audio atp.
        if (typeof ans === "string") return ans.trim() !== "";
        // fallback – už nemáme jiný typ
        return Boolean(ans);
    };

    const renderContent = () => {
        if (isLoading) {
            return <Loading/>;
        } else if (error) {
            return <ErrorMessage error={error}/>;
        } else {
            const fillTest = testIndex === "2" ? ` ${t("fillTest")}` : "";
            const firstTest = parseInt(testIndex) === 1;
            const lastQuestion = currentQuestionIndex + 1 === Object.keys(testQuestionGroups).length;
            const groupKeys = Object.keys(testQuestionGroups);

            const skipButton = !disableSkipButton && firstTest && (
                <div className="test-controls">
                    <div>
                        <p>{t("skipTestLabel")}</p>
                        <Button type="button" label={t("skipTestButton")} onClick={() => _handleSubmit(true)}/>
                    </div>
                </div>
            );

            return <>
                <div className={"container"}>
                    <p>{`${t("test")}: ${testIndex}${fillTest}`}</p>
                    <Carousel
                        showThumbs={false}
                        selectedItem={currentQuestionIndex}
                        onChange={setCurrentQuestionIndex}
                        className="test-carousel"

                        renderIndicator={(onClickHandler, isSelected, index) => {
                            if (!testQuestionGroups) return null;

                            // skupina pro daný slide
                            const group = testQuestionGroups[groupKeys[index]] || [];

                            const total = group.length;
                            const answered = group.reduce((acc, q) => acc + (isQuestionAnswered(q) ? 1 : 0), 0);

                            const percent = total ? Math.round((answered / total) * 100) : 0;

                            return (
                                <div
                                    key={index}
                                    onClick={onClickHandler}
                                    className={`my-custom-indicator ${isSelected ? "selected" : ""}`}
                                    aria-label={`Slide ${index + 1}: ${percent}% hotovo (${answered}/${total})`}
                                    title={`${percent}% hotovo (${answered}/${total})`}
                                >
                                    <span className="my-dot" style={{'--p': `${percent}%`}} data-percentage={percent}/>
                                </div>
                            );
                        }}
                    >
                        {renderQuestions()}
                    </Carousel>
                    <div className="carousel-controls">
                        <Prev onClick={() => setCurrentQuestionIndex(currentQuestionIndex - 1)}/>
                        <Next onClick={() => setCurrentQuestionIndex(currentQuestionIndex + 1)}/>
                    </div>
                    {skipButton}
                    {lastQuestion &&
                        <Button className="submit" type="button" label={t("send")} onClick={() => _handleSubmit()}/>}
                </div>
            </>
        }
    }

    return (
        <div className="Test" dir="ltr">
            <AppInfo step={testType === "sortingTest" ? 2 : 6}/>
            {renderContent()}
        </div>
    );
};

export default Test;
